import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useRole } from '@/contexts/RoleContext';
import { LogOut, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { clearLocalStoragePreservingConsent } from '@/lib/cookieConsent';
import { CookieConsentSettingsButton } from '@/components/cookie-consent/CookieConsentRoot';


export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const failsafeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // CRITICAL: Listen for SIGNED_OUT event to clear failsafe timeout
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        console.log('[AdminLayout] SIGNED_OUT detected - czyszczenie failsafe timeout');
        if (failsafeTimeoutRef.current) {
          clearTimeout(failsafeTimeoutRef.current);
          failsafeTimeoutRef.current = null;
        }
        setIsLoggingOut(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (failsafeTimeoutRef.current) {
        clearTimeout(failsafeTimeoutRef.current);
        failsafeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) {
      console.log('[AdminLayout] handleLogout: już w trakcie wylogowania, pomijam');
      return;
    }

    setIsLoggingOut(true);
    console.log('[AdminLayout] handleLogout: start, timestamp=', Date.now());
    
    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
      failsafeTimeoutRef.current = null;
    }
    
    try {
      console.log('[AdminLayout] handleLogout: wywołuję signOut...');
      await supabase.auth.signOut();
      console.log('[AdminLayout] handleLogout: signOut zakończony');
      clearLocalStoragePreservingConsent();
      
      failsafeTimeoutRef.current = setTimeout(() => {
        console.warn('[AdminLayout] handleLogout: Failsafe timeout - przejście na stronę główną');
        navigate('/', { replace: true });
      }, 2000);
    } catch (error) {
      console.log('[AdminLayout] handleLogout: CATCH error=', error);
      console.error('[AdminLayout] Nieoczekiwany błąd podczas wylogowania:', error);
      clearLocalStoragePreservingConsent();
      
      failsafeTimeoutRef.current = setTimeout(() => {
        console.warn('[AdminLayout] handleLogout: Failsafe timeout (error case) - przejście na stronę główną');
        navigate('/', { replace: true });
      }, 2000);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-white dark:bg-[#0C1020] shadow-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="text-xl font-bold hover:opacity-90 transition-opacity">
              <span className="text-gray-900 dark:text-white">DOMIO</span>
              <span className="text-[#2C7BE5] dark:text-[#60A5FA] ml-1">Cleaning</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <LanguageToggle />

            {/* Theme Toggle */}
            <CookieConsentSettingsButton className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground" />
            <ThemeToggle />

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={cn(
                "h-9 w-9 flex items-center justify-center",
                "text-muted-foreground hover:text-foreground",
                "rounded-lg border border-border/50",
                "hover:border-border hover:bg-accent",
                "transition-all duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isLoggingOut && "opacity-50 cursor-not-allowed"
              )}
              aria-label="Wyloguj się"
            >
              {isLoggingOut ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-auto">
        <div className="container mx-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
